import { useState, lazy, Suspense } from 'react';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { Box, Typography, Container, GridLegacy as Grid, Link, IconButton, useMediaQuery } from '@mui/material';
import {
    Instagram as InstagramIcon,
    LinkedIn as LinkedInIcon,
    Close as XIcon,
    YouTube as YouTubeIcon
} from '@mui/icons-material';
import { VirtolyLogo } from '../common/VirtolyLogo';
import { getButtonColor } from '../../theme/themeUtils';
import { Z_INDEX } from '../../constants/zIndex';
import { fontSizeFixed, responsiveFontSize } from '../../util/responsiveUtils';

const HowItWorksModal = lazy(() =>
    import('./HowItWorksModal').then((m) => ({ default: m.HowItWorksModal })),
);

const FooterContainer = styled(Box)(({ theme }) => ({
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(4, 0),
    marginTop: 'auto',
    // FOOTER is 10 so footer stays above background but does not cover page content (visible on home, no overlap on iOS)
    position: 'relative',
    zIndex: Z_INDEX.FOOTER,
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(6, 0),
    },
}));

const FooterLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.secondary,
    display: 'block',
    marginBottom: theme.spacing(1),
    fontSize: fontSizeFixed.rem0875,
    textDecoration: 'none',
    transition: 'color 0.2s ease-in-out',
    [theme.breakpoints.up('sm')]: {
        fontSize: fontSizeFixed.rem1,
    },
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.text.secondary, 0.1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
        width: 36,
        height: 36,
        '& .MuiSvgIcon-root': {
            fontSize: fontSizeFixed.rem1125,
        },
    },
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: fontSizeFixed.rem1,
    [theme.breakpoints.up('sm')]: {
        fontSize: fontSizeFixed.rem1125,
    },
}));

export const Footer = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);

    const handleHowItWorksClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setHowItWorksOpen(true);
    };

    const handleHowItWorksClose = () => {
        setHowItWorksOpen(false);
    };

    return (
        <FooterContainer>
            <Container maxWidth="lg">
                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    {/* Company Info - full width below md so no overlap with link columns on narrow screens */}
                    <Grid item xs={12} md={3} sx={{ minHeight: { xs: 140, sm: 160 } }}>
                        <Box display="flex" alignItems="center" mb={2} sx={{ gap: { xs: 1, sm: 2 } }}>
                            <VirtolyLogo
                                alt="Virtoly Logo"
                                sx={{
                                    '& .virtoly-logo-img': {
                                        height: { xs: 32, sm: 40 },
                                    },
                                }}
                            />
                            <Typography 
                                variant={isMobile ? "h6" : "h5"} 
                                component="div"
                                sx={{
                                    fontSize: responsiveFontSize.formTitle3Bp
                                }}
                            >
                                Virtoly
                            </Typography>
                        </Box>
                        <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            mb={2}
                            sx={{
                                fontSize: responsiveFontSize.body,
                                lineHeight: 1.6
                            }}
                        >
                            Book local home services, compare quotes, and hire trusted experts across Canada.
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            gap: { xs: 0.5, sm: 1 }
                        }}>
                            <Box
                                component="a"
                                href="https://www.instagram.com/virtoly/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textDecoration: 'none', display: 'inline-block' }}
                            >
                                <SocialIcon 
                                    aria-label="Instagram"
                                    sx={{
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                color: '#ffffff',
                                                backgroundColor: getButtonColor(theme),
                                                transform: 'translateY(-2px)',
                                            }
                                        }
                                    }}
                                >
                                    <InstagramIcon fontSize="small" />
                                </SocialIcon>
                            </Box>
                            <Box
                                component="a"
                                href="https://www.linkedin.com/company/virtoly/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textDecoration: 'none', display: 'inline-block' }}
                            >
                                <SocialIcon 
                                    aria-label="LinkedIn"
                                    sx={{
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                color: '#ffffff',
                                                backgroundColor: getButtonColor(theme),
                                                transform: 'translateY(-2px)',
                                            }
                                        }
                                    }}
                                >
                                    <LinkedInIcon fontSize="small" />
                                </SocialIcon>
                            </Box>
                            <Box
                                component="a"
                                href="https://x.com/virtoly"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textDecoration: 'none', display: 'inline-block' }}
                            >
                                <SocialIcon 
                                    aria-label="X (formerly Twitter)"
                                    sx={{
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                color: '#ffffff',
                                                backgroundColor: getButtonColor(theme),
                                                transform: 'translateY(-2px)',
                                            }
                                        }
                                    }}
                                >
                                    <XIcon fontSize="small" />
                                </SocialIcon>
                            </Box>
                            <Box
                                component="a"
                                href="https://www.youtube.com/@Virtoly"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textDecoration: 'none', display: 'inline-block' }}
                            >
                                <SocialIcon 
                                    aria-label="YouTube"
                                    sx={{
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                color: '#ffffff',
                                                backgroundColor: getButtonColor(theme),
                                                transform: 'translateY(-2px)',
                                            }
                                        }
                                    }}
                                >
                                    <YouTubeIcon fontSize="small" />
                                </SocialIcon>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Support, Discover, Company - one row; right-aligned on md+, left on narrow */}
                    <Grid item xs={12} md={9} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 }, minWidth: 0, maxWidth: '100%', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                        {/* Support */}
                        <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
                            <FooterTitle variant="subtitle1">Support</FooterTitle>
                            <FooterLink 
                                href="#" 
                                onClick={handleHowItWorksClick} 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                How does it work?
                            </FooterLink>
                            <FooterLink 
                                href="/help-center" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Help Center
                            </FooterLink>
                            <FooterLink 
                                href="/safety" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Safety
                            </FooterLink>
                            <FooterLink 
                                href="/contactUs" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Contact Us
                            </FooterLink>
                        </Box>

                        {/* Discover */}
                        <Box sx={{ flex: '0 0 auto', minWidth: 0, maxWidth: '100%' }}>
                            <FooterTitle variant="subtitle1">Discover</FooterTitle>
                            <FooterLink 
                                href="/services" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Services
                            </FooterLink>
                            <FooterLink 
                                href="/service-areas" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Service areas
                            </FooterLink>
                            <FooterLink 
                                href="/find-experts-in-city" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Find local experts
                            </FooterLink>
                        </Box>

                        {/* Company */}
                        <Box sx={{ flex: '0 0 auto', minWidth: 0, maxWidth: '100%' }}>
                            <FooterTitle variant="subtitle1">Company</FooterTitle>
                            <FooterLink 
                                href="/aboutUs" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                About Us
                            </FooterLink>
                            <FooterLink 
                                href="/careers" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Careers
                            </FooterLink>
                            <FooterLink 
                                href="/feedback" 
                                variant="body2"
                                sx={{ '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } } }}
                            >
                                Feedback
                            </FooterLink>
                        </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Bottom Copyright */}
                <Box 
                    mt={{ xs: 3, sm: 4, md: 5 }} 
                    pt={{ xs: 2, sm: 3 }} 
                    borderTop={`1px solid ${theme.palette.divider}`}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{
                                    fontSize: responsiveFontSize.caption,
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}
                            >
                                © {new Date().getFullYear()} Virtoly Inc. All rights reserved.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box 
                                display="flex" 
                                justifyContent={{ xs: 'center', sm: 'flex-end' }} 
                                flexWrap="wrap"
                                gap={{ xs: 1, sm: 2 }}
                            >
                                <FooterLink 
                                    href="/privacy" 
                                    variant="body2"
                                    sx={{ 
                                        fontSize: responsiveFontSize.caption,
                                        '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } }
                                    }}
                                >
                                    Privacy Policy
                                </FooterLink>
                                <FooterLink 
                                    href="/terms" 
                                    variant="body2"
                                    sx={{ 
                                        fontSize: responsiveFontSize.caption,
                                        '@media (hover: hover)': { '&:hover': { color: getButtonColor(theme) } }
                                    }}
                                >
                                    Terms of Service
                                </FooterLink>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            {howItWorksOpen && (
                <Suspense fallback={null}>
                    <HowItWorksModal open={howItWorksOpen} onClose={handleHowItWorksClose} />
                </Suspense>
            )}
        </FooterContainer>
    );
};